import Vue from 'vue'
import { isSamePath as _isSamePath, joinURL, normalizeURL, withQuery, withoutTrailingSlash } from 'ufo'

// window.{{globals.loadedCallback}} hook
// Useful for jsdom testing or plugins (https://github.com/tmpvar/jsdom#dealing-with-asynchronous-script-loading)
if (process.client) {
  window.onNuxtReadyCbs = []
  window.onNuxtReady = (cb) => {
    window.onNuxtReadyCbs.push(cb)
  }
}

export function createGetCounter (counterObject, defaultKey = '') {
  return function getCounter (id = defaultKey) {
    if (counterObject[id] === undefined) {
      counterObject[id] = 0
    }
    return counterObject[id]++
  }
}

export function empty () {}

export function globalHandleError (error) {
  if (Vue.config.errorHandler) {
    Vue.config.errorHandler(error)
  }
}

export function interopDefault (promise) {
  return promise.then(m => m.default || m)
}

export function hasFetch(vm) {
  return vm.$options && typeof vm.$options.fetch === 'function' && !vm.$options.fetch.length
}
export function purifyData(data) {
  if (process.env.NODE_ENV === 'production') {
    return data
  }

  return Object.entries(data).filter(
    ([key, value]) => {
      const valid = !(value instanceof Function) && !(value instanceof Promise)
      if (!valid) {
        console.warn(`${key} is not able to be stringified. This will break in a production environment.`)
      }
      return valid
    }
    ).reduce((obj, [key, value]) => {
      obj[key] = value
      return obj
    }, {})
}
export function getChildrenComponentInstancesUsingFetch(vm, instances = []) {
  const children = vm.$children || []
  for (const child of children) {
    if (child.$fetch) {
      instances.push(child)
      continue; // Don't get the children since it will reload the template
    }
    if (child.$children) {
      getChildrenComponentInstancesUsingFetch(child, instances)
    }
  }
  return instances
}

export function applyAsyncData (Component, asyncData) {
  if (
    // For SSR, we once all this function without second param to just apply asyncData
    // Prevent doing this for each SSR request
    !asyncData && Component.options.__hasNuxtData
  ) {
    return
  }

  const ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} }
  Component.options._originDataFn = ComponentData

  Component.options.data = function () {
    const data = ComponentData.call(this, this)
    if (this.$ssrContext) {
      asyncData = this.$ssrContext.asyncData[Component.cid]
    }
    return { ...data, ...asyncData }
  }

  Component.options.__hasNuxtData = true

  if (Component._Ctor && Component._Ctor.options) {
    Component._Ctor.options.data = Component.options.data
  }
}

export function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component) // fix issue #6
    Component._Ctor = Component
  } else {
    Component._Ctor = Component
    Component.extendOptions = Component.options
  }
  // If no component name defined, set file path as name, (also fixes #5703)
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file
  }
  return Component
}

export function getMatchedComponents (route, matches = false, prop = 'components') {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m[prop]).map((key) => {
      matches && matches.push(index)
      return m[prop][key]
    })
  }))
}

export function getMatchedComponentsInstances (route, matches = false) {
  return getMatchedComponents(route, matches, 'instances')
}

export function flatMapComponents (route, fn) {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m.components).reduce((promises, key) => {
      if (m.components[key]) {
        promises.push(fn(m.components[key], m.instances[key], m, key, index))
      } else {
        delete m.components[key]
      }
      return promises
    }, [])
  }))
}

export function resolveRouteComponents (route, fn) {
  return Promise.all(
    flatMapComponents(route, async (Component, instance, match, key) => {
      // If component is a function, resolve it
      if (typeof Component === 'function' && !Component.options) {
        try {
          Component = await Component()
        } catch (error) {
          // Handle webpack chunk loading errors
          // This may be due to a new deployment or a network problem
          if (
            error &&
            error.name === 'ChunkLoadError' &&
            typeof window !== 'undefined' &&
            window.sessionStorage
          ) {
            const timeNow = Date.now()
            const previousReloadTime = parseInt(window.sessionStorage.getItem('nuxt-reload'))

            // check for previous reload time not to reload infinitely
            if (!previousReloadTime || previousReloadTime + 60000 < timeNow) {
              window.sessionStorage.setItem('nuxt-reload', timeNow)
              window.location.reload(true /* skip cache */)
            }
          }

          throw error
        }
      }
      match.components[key] = Component = sanitizeComponent(Component)
      return typeof fn === 'function' ? fn(Component, instance, match, key) : Component
    })
  )
}

export async function getRouteData (route) {
  if (!route) {
    return
  }
  // Make sure the components are resolved (code-splitting)
  await resolveRouteComponents(route)
  // Send back a copy of route with meta based on Component definition
  return {
    ...route,
    meta: getMatchedComponents(route).map((Component, index) => {
      return { ...Component.options.meta, ...(route.matched[index] || {}).meta }
    })
  }
}

export async function setContext (app, context) {
  // If context not defined, create it
  if (!app.context) {
    app.context = {
      isStatic: process.static,
      isDev: false,
      isHMR: false,
      app,
      store: app.store,
      payload: context.payload,
      error: context.error,
      base: app.router.options.base,
      env: {"MOBICARREQUESTOR_ID":"16","CANONICAL_URL":"https://www.rentcars.com","BASE_URL":"https://m.rentcars.com","SITE_URL":"https://www.rentcars.com","SITE_MOBILE_URL":"https://m.rentcars.com","RENTCARS_CDN":"https://static-rentcars-cdn.rentcars.com","MOBILE_CDN":"https://static-mobile-cdn.rentcars.com","RWORKER_ENDPOINT":"https://rworker.rentcars.com/v2/request-rest/","FACADE_ENDPOINT":"https://facade.rentcars.com","INTERNAL_ENDPOINT":"https://facade-internal.rentcars.com","FACADE_APIKEY":"YXBpa2V5ZG9tb2JpbGVkZXZlbG9wbWVudAo","FACADE_TIMEOUT":"60000","DEBUG":"false","DEBUG_SECRET":"r3ntc4rs","SENTRY_DSN":"https://31c708229b934fcdb1b251d5d6b0a175@sentry.io/1191718","WOOPRA_DOMAIN":"rentcars.com","NODE_ENV":"production","GTM_ID":"GTM-W7BGZM5","GTM_ID_AMP":"GTM-KGWKR4C","TOKEN_LP":"a7eac2ff27b01309a89e7e988c5d3f43","WORLDPAY_PUBLIC_KEY":"1#10001#9bf7c56a01c74afd469713a3b6f0f8892d5411e67395e7a107417ce25d8fd45a83181e115d61e013851cd81cfded41a5dce9f01500b036c771d7d3358b4e485d0bcb8a09980cca36f5377bc969f2ea23d2316096a0f0b3ed5a9be1f253c096c3b0ec8e0acd89c3c38d48551e7a95b636b9bdbd5841b2468175a24bb4f9d53c5759836f37b67714232a0ceeb5b44d45e290cb72828c97ad07cc6fcba44777d3cab04ff3541af788c2ae96af3b52ddb26fde421293f3cd19bf84a42e2879cc974d2df9ad74055aca2dfc4020fb6cd74623304e7a8c8f1ec434d0a36e0e28b2165e24bb9fdfd95fbe41e9ee6822caea4cc3a716fc5fef2949203e9b0a95a06e7dc9","PUBLIC_PATH":"//static-mobile-cdn.rentcars.com/dist/pwa/dist/client/","NEWRELIC_KEY":"8de9ab9a9bfb390ca510b9fe8923fd4530ea3ffc","CDN_PROMOTION":"//promotions.rentcars.com","FACEBOOK_APP_ID":"306924319471378","GOOGLE_MAPS_API_KEY":"AIzaSyCsBKuS_ue-1MFDahkU98WgRAs_6kKtNqo","RECAPTCHA_SITE_KEY":"6Lelb60UAAAAAOVMxZv9ABRnvHLcsYEqQwluRYRx","CMS_BANNER_ENDPOINT":"https://cms-banner.rentcars.com/v1/campaigns/component","APPLE_APP_ID":"com.Mobicar.RentCars.service","APPLE_APP_REDIRECT_URL":"https://m.rentcars.com/pt-br/cliente","ADYEN_PUBLIC_KEY":"live_6ZOXRBYDPZE2NPZWQ5QTPDW5PEMSIVUY","USER_CONTROL_ENDPOINT":"https://users.rentcars.com","USER_CONTROL_TIMEOUT":"60000","USER_CONTROL_JWT_TOKEN":"d8d8fff3ce4426eae81a68350be1c51812d864aff5bf6a73871240fedfe3f90a0728c8314cc61676e1fdb1dbb2dd64f1669dc0e92e77edfa25d16025b7fcdbc1","USER_CONTROL_REQUESTOR_DEVICE":"Mobile","USER_CONTROL_TOKEN":"80c34e7dcff057796cd7a4cab331385e25a28b89e33506039f59e635b97915e5","USER_CONTROL_AUTH_VERSION":"v1","GTM_ID_OPTIMIZE":"OPT-W2N52N6","DLOCAL_KEY":"ad2147e8-320c-4fb5-91a7-e1a821ee5676","DLOCAL_KEY_MX_BR":"da1b805c-0f17-4061-96da-b9df5fb5a19c","DLOCAL_KEY_MX_NL":"4676a61d-a0fa-416f-ad16-1dbdc7df3e4a","DLOCAL_KEY_AR_BR":"572a5a36-b1ba-4391-b7e1-e973966c70cb","DLOCAL_KEY_AR_NL":"608fbeb6-78d2-485f-a852-3a16b1da7f17","DLOCAL_KEY_NL":"fd228fe3-23fb-4ff2-995e-4a08c7287bca","DLOCAL_KEY_CO_BR":"94297179-28be-49c4-9b20-f81f9a30d80a","DLOCAL_KEY_CO_NL":"a208c827-a28c-4c02-bfc5-4a4c3963ea44","DLOCAL_KEY_CL_BR":"5b2077d2-4c32-4287-a424-af4e30a0b8cb","DLOCAL_KEY_CL_NL":"56dd7f6c-2798-4bcf-8f59-cae951b3b2ba","DLOCAL_KEY_PE_BR":"c1f19eaa-6617-431b-96bb-11ad2f6d41df","DLOCAL_KEY_PE_NL":"26ff7dea-c428-4846-8838-97ccb45c7775","DLOCAL_KEY_UY_BR":"fed57e8d-8e24-4efa-a1e3-e220857fdb60","DLOCAL_KEY_UY_NL":"ceeaa76c-11d9-4731-a653-2f6f28c0019b","BUGSNAG_API_KEY":"e02900631b2787bffa101cde6214665f","REST_HOST":"https://services.rentcars.com/product/api/","ANNUAL_SUBSCRIPTION_API":"https://api.rentcars.com","FAQ_HOST":"https://api.rentcars.com/faq/","FEATURE_FLAG_API":"https://api.rentcars.com/feature-flag/","GOOGLE_SIGN_IN_CLIENT_ID":"249486929087-6eivg9p0es2nji563po57nod1ch9fpik.apps.googleusercontent.com","GOOGLE_MERCHANT_ID":"BCR2DN4T7CLM3ZZW","RECAPTCHAV3_SITE_KEY":"6Ld-IfUfAAAAAFuqQiZzL5M26MXvWjSIwRU-9oC0","RECAPTCHAV3_ENTERPRISE_SITE_KEY":"6Lc5KswgAAAAAKvCwkt8w7f58jaoSDF4ZUubn2Q7","HCAPTCHA":"5289a996-208a-40dd-9730-3319289f75c4","GRECAPTCHA_ENDPOINT":"https://www.google.com/recaptcha/enterprise.js","HCAPTCHA_ENDPOINT":"https://js.hcaptcha.com/1/api.js","COUNT_RENTAL_STORES":"350","COUNT_COUNTRIES":"160","WEBSOCKET_URL":"wss://websocketv2.rentcars.com","CARD_NUVEI_MERCHANT_SITE_ID":"242868","CARD_NUVEI_MERCHANT_ID":"3123824650267412864","REST_SESSION_TOKEN_NUVEI_ENDPOINT":"https://rest.rentcars.com/api/v5/payments/gatewaySessionToken","CIELO_3DS_DEBUG":"false","CIELO_3DS_ENVIRONMENT":"PRD","CIELO_3DS_SCRIPT":"https://mpi.braspag.com.br/Scripts/BP.Mpi.3ds20.min.js","VWO_SDK_KEY":"5ff27874309e33cac52e0b1b67f1cb02","VWO_ACCOUNT_ID":"762500","SIFT_SCIENCE_KEY":"f573b92208","PARTNER_ID_GYG":"U4HO51C","INTERCOM_URL":"https://api-iam.intercom.io","INTERCOM_APP_ID_TEST":"yatfen02","INTERCOM_APP_ID_PROD":"igxqrc9i","DD_APPLICATION_ID":"2517bada-ef61-4f66-b185-09b5c7efd186","DD_CLIENT_TOKEN":"pubf98539d820716f471d304a2d88ad47a4","DD_VERSION":"1.0.0","DD_ENV":"prod","PHRASE_ACCOUNT_ID":"0f364fc2","PHRASE_PROJECT_ID":"be22dec3e43c93b3e561bc75e5a0ff89","PHRASE_STATUS":"disabled","MULTISTORE_API":"https://api.rentcars.com/multilocation/","LOCALIDADE_API":"https://api-localidades.rentcars.com/api","RENT_PUBLIC_API":"https://api.rentcars.com/product/api","VWO_FME_SDK_KEY":"dd3f87ebb1b56e590689d1e82654b1b7","VWO_FME_ACCOUNT_ID":"762500"}
    }
    // Only set once

    if (context.req) {
      app.context.req = context.req
    }
    if (context.res) {
      app.context.res = context.res
    }

    if (context.ssrContext) {
      app.context.ssrContext = context.ssrContext
    }
    app.context.redirect = (status, path, query) => {
      if (!status) {
        return
      }
      app.context._redirected = true
      // if only 1 or 2 arguments: redirect('/') or redirect('/', { foo: 'bar' })
      let pathType = typeof path
      if (typeof status !== 'number' && (pathType === 'undefined' || pathType === 'object')) {
        query = path || {}
        path = status
        pathType = typeof path
        status = 302
      }
      if (pathType === 'object') {
        path = app.router.resolve(path).route.fullPath
      }
      // "/absolute/route", "./relative/route" or "../relative/route"
      if (/(^[.]{1,2}\/)|(^\/(?!\/))/.test(path)) {
        app.context.next({
          path,
          query,
          status
        })
      } else {
        path = withQuery(path, query)
        if (process.server) {
          app.context.next({
            path,
            status
          })
        }
        if (process.client) {
          // https://developer.mozilla.org/en-US/docs/Web/API/Location/replace
          window.location.replace(path)

          // Throw a redirect error
          throw new Error('ERR_REDIRECT')
        }
      }
    }
    if (process.server) {
      app.context.beforeNuxtRender = fn => context.beforeRenderFns.push(fn)
    }
    if (process.client) {
      app.context.nuxtState = window.__NUXT__
    }
  }

  // Dynamic keys
  const [currentRouteData, fromRouteData] = await Promise.all([
    getRouteData(context.route),
    getRouteData(context.from)
  ])

  if (context.route) {
    app.context.route = currentRouteData
  }

  if (context.from) {
    app.context.from = fromRouteData
  }

  app.context.next = context.next
  app.context._redirected = false
  app.context._errored = false
  app.context.isHMR = false
  app.context.params = app.context.route.params || {}
  app.context.query = app.context.route.query || {}
}

export function middlewareSeries (promises, appContext) {
  if (!promises.length || appContext._redirected || appContext._errored) {
    return Promise.resolve()
  }
  return promisify(promises[0], appContext)
    .then(() => {
      return middlewareSeries(promises.slice(1), appContext)
    })
}

export function promisify (fn, context) {
  let promise
  if (fn.length === 2) {
    // fn(context, callback)
    promise = new Promise((resolve) => {
      fn(context, function (err, data) {
        if (err) {
          context.error(err)
        }
        data = data || {}
        resolve(data)
      })
    })
  } else {
    promise = fn(context)
  }

  if (promise && promise instanceof Promise && typeof promise.then === 'function') {
    return promise
  }
  return Promise.resolve(promise)
}

// Imported from vue-router
export function getLocation (base, mode) {
  if (mode === 'hash') {
    return window.location.hash.replace(/^#\//, '')
  }

  base = decodeURI(base).slice(0, -1) // consideration is base is normalized with trailing slash
  let path = decodeURI(window.location.pathname)

  if (base && path.startsWith(base)) {
    path = path.slice(base.length)
  }

  const fullPath = (path || '/') + window.location.search + window.location.hash

  return normalizeURL(fullPath)
}

// Imported from path-to-regexp

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
export function compile (str, options) {
  return tokensToFunction(parse(str, options), options)
}

export function getQueryDiff (toQuery, fromQuery) {
  const diff = {}
  const queries = { ...toQuery, ...fromQuery }
  for (const k in queries) {
    if (String(toQuery[k]) !== String(fromQuery[k])) {
      diff[k] = true
    }
  }
  return diff
}

export function normalizeError (err) {
  let message
  if (!(err.message || typeof err === 'string')) {
    try {
      message = JSON.stringify(err, null, 2)
    } catch (e) {
      message = `[${err.constructor.name}]`
    }
  } else {
    message = err.message || err
  }
  return {
    ...err,
    message,
    statusCode: (err.statusCode || err.status || (err.response && err.response.status) || 500)
  }
}

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
const PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g')

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  const tokens = []
  let key = 0
  let index = 0
  let path = ''
  const defaultDelimiter = (options && options.delimiter) || '/'
  let res

  while ((res = PATH_REGEXP.exec(str)) != null) {
    const m = res[0]
    const escaped = res[1]
    const offset = res.index
    path += str.slice(index, offset)
    index = offset + m.length

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1]
      continue
    }

    const next = str[index]
    const prefix = res[2]
    const name = res[3]
    const capture = res[4]
    const group = res[5]
    const modifier = res[6]
    const asterisk = res[7]

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path)
      path = ''
    }

    const partial = prefix != null && next != null && next !== prefix
    const repeat = modifier === '+' || modifier === '*'
    const optional = modifier === '?' || modifier === '*'
    const delimiter = res[2] || defaultDelimiter
    const pattern = capture || group

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter,
      optional,
      repeat,
      partial,
      asterisk: Boolean(asterisk),
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    })
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index)
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path)
  }

  return tokens
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str, slashAllowed) {
  const re = slashAllowed ? /[?#]/g : /[/?#]/g
  return encodeURI(str).replace(re, (c) => {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURIComponentPretty(str, true)
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$/()])/g, '\\$1')
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens, options) {
  // Compile all the tokens into regexps.
  const matches = new Array(tokens.length)

  // Compile all the patterns before compilation.
  for (let i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$', flags(options))
    }
  }

  return function (obj, opts) {
    let path = ''
    const data = obj || {}
    const options = opts || {}
    const encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (typeof token === 'string') {
        path += token

        continue
      }

      const value = data[token.name || 'pathMatch']
      let segment

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (Array.isArray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (let j = 0; j < value.length; j++) {
          segment = encode(value[j])

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value)

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment
    }

    return path
  }
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options && options.sensitive ? '' : 'i'
}

export function addLifecycleHook(vm, hook, fn) {
  if (!vm.$options[hook]) {
    vm.$options[hook] = []
  }
  if (!vm.$options[hook].includes(fn)) {
    vm.$options[hook].push(fn)
  }
}

export const urlJoin = joinURL

export const stripTrailingSlash = withoutTrailingSlash

export const isSamePath = _isSamePath

export function setScrollRestoration (newVal) {
  try {
    window.history.scrollRestoration = newVal;
  } catch(e) {}
}
